import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useRef, useState, type FormEvent, type ReactNode } from 'react'

import { Button } from '../../../components/Button'
import { useAuth } from '../../AuthContext'
import {
  TRANSPORT_CONFIG_KEY,
  fetchSettings,
  setSetting,
  type Setting,
} from '../../api/transportConfig'
import {
  SETTING_COPY,
  fromSettingInput,
  settingInput,
  type SettingCopy,
} from '../../transportUnits'
import { SavedNote } from './SavedNote'
import { SectionState } from './SectionState'
import { errorMessage, retryUnlessForbidden } from './errorMessage'
import { INPUT } from './styles'
import { useSyncedText } from './useSyncedText'

/**
 * The staff-editable numbers (spec §F.2 "Settings"), each in the unit the desk
 * thinks in: notice and the cut-off in hours, the rest as labelled. The range
 * is checked here in the same unit, so the message reads "between 1 and 48
 * hours" rather than the server's minutes; the server checks it again.
 */
export function SettingsSection(): ReactNode {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'settings'],
    queryFn: () => fetchSettings(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const state = (
    <SectionState
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      what="settings"
    />
  )
  if (!query.data) return state
  const rows = query.data.flatMap((s) => {
    const copy = SETTING_COPY[s.key]
    // A key the page has no words for is not shown rather than shown raw.
    return copy ? [{ setting: s, copy }] : []
  })
  if (rows.length === 0) {
    return <p className="text-sm text-ink-2">No settings to show.</p>
  }
  return (
    <ul className="flex flex-col divide-y divide-line-1 rounded-[12px] border border-line-1">
      {rows.map(({ setting, copy }) => (
        <SettingRow key={setting.key} setting={setting} copy={copy} />
      ))}
    </ul>
  )
}

function SettingRow({
  setting,
  copy,
}: {
  setting: Setting
  copy: SettingCopy
}): ReactNode {
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const input = useRef<HTMLInputElement>(null)
  const [text, setText] = useSyncedText(
    settingInput(setting.key, setting.value),
  )
  const [problem, setProblem] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const save = useMutation({
    mutationFn: (value: number) => setSetting(token, setting.key, value),
    onSuccess: () => {
      setProblem(null)
      setSaved(true)
      input.current?.focus()
      void queryClient.invalidateQueries({ queryKey: TRANSPORT_CONFIG_KEY })
    },
    onError: (e) => setProblem(errorMessage(e)),
  })

  const typed = fromSettingInput(setting.key, text)
  const dirty = typed !== setting.value
  const low = settingInput(setting.key, setting.minimum)
  const high = settingInput(setting.key, setting.maximum)
  const range = `Between ${low} and ${high} ${copy.unit}.`

  function submit(e: FormEvent): void {
    e.preventDefault()
    if (!dirty) return
    if (typed === null) {
      setProblem(
        copy.inHours
          ? 'Use hours, e.g. 12 or 1.5 (whole minutes only).'
          : `Use a whole number of ${copy.unit}.`,
      )
      return
    }
    if (typed < setting.minimum || typed > setting.maximum) {
      setProblem(range)
      return
    }
    save.mutate(typed)
  }

  const id = `setting-${setting.key}`
  const hintId = `${id}-hint`
  return (
    <li className="px-4 py-3 text-sm">
      <form
        onSubmit={submit}
        className="flex flex-wrap items-center gap-x-4 gap-y-2"
      >
        <span className="min-w-0 flex-1">
          <label htmlFor={id} className="font-medium text-ink-1">
            {copy.label}
          </label>
          <span id={hintId} className="block text-xs text-ink-3">
            {copy.hint ? `${copy.hint} ` : ''}
            {range}
          </span>
        </span>
        <span className="flex items-center gap-2">
          <input
            ref={input}
            id={id}
            className={`${INPUT} w-20`}
            inputMode="decimal"
            value={text}
            aria-describedby={hintId}
            aria-invalid={problem ? true : undefined}
            onChange={(e) => {
              setText(e.target.value)
              setProblem(null)
              setSaved(false)
            }}
          />
          <span className="text-xs text-ink-3">{copy.unit}</span>
        </span>
        {dirty && (
          <Button
            kind="secondary"
            type="submit"
            loading={save.isPending}
            aria-label={`Save ${copy.label}`}
          >
            Save
          </Button>
        )}
        <SavedNote show={saved && !dirty} />
        {problem && (
          <span role="alert" className="w-full text-xs text-bed-bad">
            {problem}
          </span>
        )}
      </form>
    </li>
  )
}
